import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/api';
import type { Agency, EnforcementOfficer, ParkingZone } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader } from '../../ui/components';

const AGENCIES: Agency[] = ['SLTA', 'POLICE'];

export function OfficersAdmin() {
  const zonesQ = useApiQuery<{ items: ParkingZone[] }>('/api/v1/parking/zones');
  const zones = zonesQ.data?.items ?? [];

  const [personId, setPersonId] = useState('');
  const [badgeNumber, setBadgeNumber] = useState('');
  const [agency, setAgency] = useState<Agency>('SLTA');
  const [assignedZoneIds, setAssignedZoneIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // m46-parking exposes no GET /officers list route (only POST /officers,
  // GET /officers/lookup [requireService()-only], GET /officers/status-check,
  // and POST /officers/:id/notice-blocks — see routes/officers.ts) — so
  // there is no way for this page to fetch a real officer registry. Rather
  // than fabricate a fake list endpoint, this accumulates officers
  // successfully registered THIS session, labelled honestly as such.
  const [registeredThisSession, setRegisteredThisSession] = useState<EnforcementOfficer[]>([]);

  const toggleZone = (zoneId: string) => {
    setAssignedZoneIds((prev) => (prev.includes(zoneId) ? prev.filter((z) => z !== zoneId) : [...prev, zoneId]));
  };

  const submit = async () => {
    setError(null);
    if (!personId.trim() || !badgeNumber.trim()) {
      setError('Person ID and badge number are required.');
      return;
    }
    setBusy(true);
    try {
      const officer = await api.post<EnforcementOfficer>('/api/v1/parking/officers', {
        personId: personId.trim(),
        badgeNumber: badgeNumber.trim(),
        agency,
        assignedZoneIds,
      });
      setRegisteredThisSession((prev) => [officer, ...prev]);
      setPersonId('');
      setBadgeNumber('');
      setAssignedZoneIds([]);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not register officer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Officer Registry" subtitle="Register SLTA and Police enforcement officers (PARKING_ADMIN only)." />

      <div className="mb-6">
        <InfoNote message="m46-parking has no endpoint to list registered officers — only to register one (POST /officers), look one up by personId with a service token, or run a status check. This page therefore shows officers registered in this browser session, not a full registry; reloading the page clears the list below (the officers themselves remain registered on the server)." />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Register officer</h2>
          {error ? (
            <div className="mb-3">
              <ErrorNote message={error} />
            </div>
          ) : null}
          <div className="space-y-3">
            <Field label="Person ID" hint="One Citizen NIN/sub — becomes this officer's EnforcementOfficer.personId.">
              <input className="field-input" value={personId} onChange={(e) => setPersonId(e.target.value)} placeholder="e.g. 994-1004-1-1-04" />
            </Field>
            <Field label="Badge number">
              <input className="field-input" value={badgeNumber} onChange={(e) => setBadgeNumber(e.target.value)} placeholder="e.g. SLTA-0103" />
            </Field>
            <Field label="Agency">
              <select className="field-input" value={agency} onChange={(e) => setAgency(e.target.value as Agency)}>
                {AGENCIES.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </Field>
            <div>
              <div className="field-label">Assigned zones</div>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
                {zones.length === 0 ? (
                  <div className="px-2 py-1 text-xs text-slate-400">No zones registered yet.</div>
                ) : (
                  zones.map((z) => (
                    <label key={z.id} className="flex items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-50">
                      <input type="checkbox" checked={assignedZoneIds.includes(z.id)} onChange={() => toggleZone(z.id)} />
                      {z.code} — {z.name}
                    </label>
                  ))
                )}
              </div>
            </div>
            <button className="btn-primary w-full" onClick={submit} disabled={busy || !personId.trim() || !badgeNumber.trim()}>
              {busy ? 'Registering…' : 'Register officer'}
            </button>
          </div>
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Registered this session ({registeredThisSession.length})</h2>
          {registeredThisSession.length === 0 ? (
            <p className="text-xs text-slate-400">Officers you register above will appear here.</p>
          ) : (
            <ul className="space-y-2">
              {registeredThisSession.map((o) => (
                <li key={o.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                  <div className="font-semibold text-slate-700">
                    {o.badgeNumber} · {o.agency}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-slate-400">
                    {o.personId} · id {o.id}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-400">
                    Zones: {o.assignedZoneIds.length > 0 ? o.assignedZoneIds.length : 'none'} · registered {formatDateTime(o.createdAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
